import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getNotifLogs,
  getNotifConfigs,
  createNotifConfig,
  updateNotifConfig,
  toggleNotifConfig,
  deleteNotifConfig,
  enviarNotifAhora,
} from '../services/adminApi';
import { showError, showSuccess } from '../utils/alerts';
import '../styles/pages/NotificacionesPage.css';

const FRECUENCIAS = ['diaria', 'semanal', 'quincenal', 'mensual'];

const DIAS_SEMANA = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' },
];

const formatFecha = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Santiago' });
};

const emptyForm = { nombre: '', asunto: '', mensaje: '', frecuencia: 'mensual', diaEnvio: 1, destinatarioTipo: 'cliente', soloConciliacion: false };

// ─── Modal Configurar ────────────────────────────────────────────────────────

function ConfigModal({ config, onSave, onClose }) {
  const [form, setForm] = useState(
    config
      ? {
          nombre: config.nombre,
          asunto: config.asunto,
          mensaje: config.mensaje,
          frecuencia: config.frecuencia,
          diaEnvio: config.dia_envio ?? 1,
          destinatarioTipo: config.destinatario_tipo ?? 'cliente',
          soloConciliacion: config.solo_conciliacion ?? false,
        }
      : { ...emptyForm }
  );
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  const showDia = form.frecuencia === 'semanal' || form.frecuencia === 'mensual';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <h3>{config ? 'Editar notificación' : 'Nueva notificación'}</h3>
        <form onSubmit={handleSubmit} className="notif-form">
          <div className="form-group">
            <label>Destinatarios</label>
            <div className="destinatario-toggle">
              <button
                type="button"
                className={`toggle-opt ${form.destinatarioTipo === 'cliente' ? 'active' : ''}`}
                onClick={() => setForm((f) => ({ ...f, destinatarioTipo: 'cliente', soloConciliacion: false }))}
              >
                Clientes
              </button>
              <button
                type="button"
                className={`toggle-opt ${form.destinatarioTipo === 'empresa' ? 'active' : ''}`}
                onClick={() => setForm((f) => ({ ...f, destinatarioTipo: 'empresa', soloConciliacion: false }))}
              >
                Empresas
              </button>
            </div>
            {form.destinatarioTipo === 'empresa' && (
              <label className="conciliacion-check">
                <input
                  type="checkbox"
                  checked={form.soloConciliacion}
                  onChange={(e) => setForm((f) => ({ ...f, soloConciliacion: e.target.checked }))}
                />
                Solo empresas con conciliación bancaria
              </label>
            )}
          </div>
          <div className="form-group">
            <label>Nombre interno</label>
            <input name="nombre" value={form.nombre} onChange={handleChange} placeholder="Ej: Recordatorio cartola" required />
          </div>
          <div className="form-group">
            <label>Asunto del email</label>
            <input name="asunto" value={form.asunto} onChange={handleChange} placeholder="Ej: Recuerda enviar tu cartola bancaria" required />
          </div>
          <div className="form-group">
            <label>Mensaje</label>
            <textarea name="mensaje" value={form.mensaje} onChange={handleChange} rows={4} placeholder="Texto del correo..." required />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Frecuencia</label>
              <select name="frecuencia" value={form.frecuencia} onChange={handleChange}>
                {FRECUENCIAS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            {showDia && (
              <div className="form-group">
                <label>{form.frecuencia === 'semanal' ? 'Día de la semana' : 'Día del mes'}</label>
                {form.frecuencia === 'semanal' ? (
                  <select name="diaEnvio" value={form.diaEnvio} onChange={handleChange}>
                    {DIAS_SEMANA.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                ) : (
                  <input type="number" name="diaEnvio" value={form.diaEnvio} onChange={handleChange} min={1} max={28} />
                )}
              </div>
            )}
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Tab Monitor ─────────────────────────────────────────────────────────────

function TabMonitor() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ tipo: '', estado: '', fechaDesde: '', fechaHasta: '' });
  const [applied, setApplied] = useState({});
  const LIMIT = 50;

  const fetchLogs = useCallback(async (p, f) => {
    setLoading(true);
    try {
      const data = await getNotifLogs({ page: p, limit: LIMIT, ...f });
      setLogs(data.logs);
      setTotal(data.total);
    } catch {
      showError('Error cargando registros');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLogs(1, {}); }, [fetchLogs]);

  const handleApply = () => {
    setPage(1);
    setApplied({ ...filters });
    fetchLogs(1, filters);
  };

  const handleClear = () => {
    const empty = { tipo: '', estado: '', fechaDesde: '', fechaHasta: '' };
    setFilters(empty);
    setApplied({});
    setPage(1);
    fetchLogs(1, {});
  };

  const handlePage = (p) => {
    setPage(p);
    fetchLogs(p, applied);
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="tab-content">
      <div className="filters-bar">
        <input
          placeholder="Tipo"
          value={filters.tipo}
          onChange={(e) => setFilters((f) => ({ ...f, tipo: e.target.value }))}
        />
        <select value={filters.estado} onChange={(e) => setFilters((f) => ({ ...f, estado: e.target.value }))}>
          <option value="">Todos los estados</option>
          <option value="sent">Enviado</option>
          <option value="failed">Fallido</option>
        </select>
        <input type="date" value={filters.fechaDesde} onChange={(e) => setFilters((f) => ({ ...f, fechaDesde: e.target.value }))} />
        <input type="date" value={filters.fechaHasta} onChange={(e) => setFilters((f) => ({ ...f, fechaHasta: e.target.value }))} />
        <button className="btn-primary btn-small" onClick={handleApply}>Filtrar</button>
        <button className="btn-secondary btn-small" onClick={handleClear}>Limpiar</button>
      </div>

      {loading ? (
        <div className="loading">Cargando...</div>
      ) : (
        <>
          <div className="log-count">{total.toLocaleString()} registros</div>
          <table className="notif-table">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Destinatario</th>
                <th>Cliente</th>
                <th>Estado</th>
                <th>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan={5} className="empty">Sin registros</td></tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id}>
                    <td>{log.tipo}</td>
                    <td>{log.destinatario_email}</td>
                    <td>{log.cliente_nombre || '—'}</td>
                    <td>
                      <span className={`badge badge-${log.estado}`}>
                        {log.estado === 'sent' ? 'Enviado' : 'Fallido'}
                      </span>
                    </td>
                    <td>{formatFecha(log.sent_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="pagination">
              <button disabled={page === 1} onClick={() => handlePage(page - 1)}>← Anterior</button>
              <span>Página {page} de {totalPages}</span>
              <button disabled={page === totalPages} onClick={() => handlePage(page + 1)}>Siguiente →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Tab Recurrentes ─────────────────────────────────────────────────────────

function TabRecurrentes() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | config-object
  const [sending, setSending] = useState(null);

  const fetchConfigs = useCallback(async () => {
    try {
      const data = await getNotifConfigs();
      setConfigs(data);
    } catch {
      showError('Error cargando notificaciones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const handleToggle = async (config) => {
    try {
      await toggleNotifConfig(config.id, !config.activo);
      setConfigs((prev) => prev.map((c) => c.id === config.id ? { ...c, activo: !c.activo } : c));
    } catch {
      showError('Error actualizando estado');
    }
  };

  const handleSave = async (form) => {
    try {
      if (modal && modal.id) {
        const updated = await updateNotifConfig(modal.id, form);
        setConfigs((prev) => prev.map((c) => c.id === modal.id ? updated : c));
        showSuccess('Notificación actualizada');
      } else {
        const created = await createNotifConfig(form);
        setConfigs((prev) => [created, ...prev]);
        showSuccess('Notificación creada');
      }
      setModal(null);
    } catch {
      showError('Error guardando notificación');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta notificación?')) return;
    try {
      await deleteNotifConfig(id);
      setConfigs((prev) => prev.filter((c) => c.id !== id));
      showSuccess('Notificación eliminada');
    } catch {
      showError('Error eliminando notificación');
    }
  };

  const handleEnviarAhora = async (config) => {
    const destLabel = config.destinatario_tipo === 'empresa'
      ? config.solo_conciliacion ? 'empresas con conciliación bancaria' : 'todas las empresas'
      : 'todos los clientes';
    if (!window.confirm(`¿Enviar "${config.nombre}" ahora a ${destLabel}?`)) return;
    setSending(config.id);
    try {
      const res = await enviarNotifAhora(config.id);
      showSuccess(`Envío completado: ${res.enviados} enviados, ${res.fallidos} fallidos`);
      fetchConfigs();
    } catch {
      showError('Error ejecutando envío');
    } finally {
      setSending(null);
    }
  };

  if (loading) return <div className="loading">Cargando...</div>;

  return (
    <div className="tab-content">
      <div className="recurrentes-header">
        <button className="btn-primary" onClick={() => setModal('new')}>+ Nueva notificación</button>
      </div>

      {configs.length === 0 ? (
        <div className="empty-state">No hay notificaciones configuradas.</div>
      ) : (
        <div className="config-list">
          {configs.map((config) => (
            <div key={config.id} className={`config-card ${config.activo ? 'activo' : 'inactivo'}`}>
              <div className="config-info">
                <div className="config-nombre">
                  {config.nombre}
                  <span className={`badge-tipo badge-tipo-${config.destinatario_tipo ?? 'cliente'}`}>
                    {config.destinatario_tipo === 'empresa'
                      ? config.solo_conciliacion
                        ? 'Empresas · Conciliación'
                        : 'Empresas'
                      : 'Clientes'}
                  </span>
                </div>
                <div className="config-meta">
                  <span>{config.frecuencia}</span>
                  {config.dia_envio && <span>· día {config.dia_envio}</span>}
                </div>
                <div className="config-fechas">
                  <span>Último envío: {formatFecha(config.ultima_ejecucion)}</span>
                  <span>Próximo: {formatFecha(config.proxima_ejecucion)}</span>
                </div>
              </div>
              <div className="config-actions">
                <label className="toggle-switch" title={config.activo ? 'Desactivar' : 'Activar'}>
                  <input type="checkbox" checked={config.activo} onChange={() => handleToggle(config)} />
                  <span className="toggle-slider" />
                </label>
                <button className="btn-small btn-secondary" onClick={() => setModal(config)}>Configurar</button>
                <button
                  className="btn-small btn-enviar"
                  onClick={() => handleEnviarAhora(config)}
                  disabled={sending === config.id}
                >
                  {sending === config.id ? 'Enviando...' : 'Enviar ahora'}
                </button>
                <button className="btn-small btn-danger" onClick={() => handleDelete(config.id)}>Eliminar</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <ConfigModal
          config={modal === 'new' ? null : modal}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function NotificacionesPage() {
  const [tab, setTab] = useState('monitor');
  const navigate = useNavigate();

  return (
    <div className="notificaciones-page">
      <header className="admin-header">
        <div className="header-content">
          <div className="header-left">
            <button className="btn-back" onClick={() => navigate('/admin/clientes')}>← Volver</button>
            <h1>Notificaciones</h1>
          </div>
        </div>
      </header>

      <div className="tabs-bar">
        <button className={`tab-btn ${tab === 'monitor' ? 'active' : ''}`} onClick={() => setTab('monitor')}>
          Monitor de envíos
        </button>
        <button className={`tab-btn ${tab === 'recurrentes' ? 'active' : ''}`} onClick={() => setTab('recurrentes')}>
          Notificaciones recurrentes
        </button>
      </div>

      {tab === 'monitor' ? <TabMonitor /> : <TabRecurrentes />}
    </div>
  );
}
